import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, MVP_FRICTIONLESS } from '@/hooks/useAuth';
import { Button } from '@/components/ui';
import { sendPasswordResetForIdentifier } from '@/lib/auth/client-auth-service';
import { runtimeEnv } from '@/lib/runtime-env';

export function AuthPage() {
  const navigate = useNavigate();
  const { firebaseUser, user, loading, authError, signIn, signInWithPassword, signOut } = useAuth();
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [resetOpen, setResetOpen] = React.useState(false);
  const [resetIdentifier, setResetIdentifier] = React.useState('');
  const [resetSubmitting, setResetSubmitting] = React.useState(false);
  const [resetMessage, setResetMessage] = React.useState<string | null>(null);
  const [resetError, setResetError] = React.useState<string | null>(null);
  const [seeding, setSeeding] = React.useState(false);
  const [seedError, setSeedError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!loading) {
      if (MVP_FRICTIONLESS) {
        navigate('/setup', { replace: true });
      } else if (!!firebaseUser && !!user) {
        navigate('/setup', { replace: true });
      }
    }
  }, [loading, firebaseUser, user, navigate]);

  if (loading || MVP_FRICTIONLESS) {
    return (
      <div className="min-h-screen bg-bg-app flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl font-black text-brand-primary mb-6">PK</div>
          <div className="text-text-muted">
            {MVP_FRICTIONLESS ? 'Frictionless Mode Active - Redirecting...' : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-app flex flex-col items-center justify-center p-6">
      <div className="w-20 h-20 rounded-2xl bg-brand-primary flex items-center justify-center text-4xl font-black text-bg-app mb-8">PK</div>

      <h1 className="text-3xl font-extrabold text-text-main mb-2 text-center">
        Poker Knights
      </h1>
      <p className="text-text-muted text-sm mb-12 text-center max-w-[300px]">
        Private poker session manager for your group
      </p>

      {firebaseUser ? (
        <div className="text-center w-full max-w-[320px]">
          <div className="mb-6">
            <img
              src={firebaseUser.photoURL ?? undefined}
              alt={firebaseUser.displayName ?? ''}
              className="w-[72px] h-[72px] rounded-full mb-3"
            />
            <div className="font-bold text-text-main text-lg">
              {firebaseUser.displayName}
            </div>
            <div className="text-text-muted text-sm">{firebaseUser.email}</div>
          </div>

          {!user && (
            <div className="bg-surface rounded-xl p-5 mb-6 text-center">
              <div className="text-brand-primary text-sm mb-2">
                {authError ? 'Sign-in Blocked' : 'Account Setup Required'}
              </div>
              <div className="text-text-muted text-xs">
                {authError || 'Ask an admin to add you to the group, or contact your host.'}
              </div>
            </div>
          )}

          <Button variant="secondary" fullWidth onClick={signOut}>
            Sign Out
          </Button>
        </div>
      ) : (
        <div className="w-full max-w-[320px] space-y-3">
          <form
            className="space-y-3"
            onSubmit={async (event) => {
              event.preventDefault();
              setSubmitting(true);
              try {
                await signInWithPassword(identifier, password);
              } catch {
                // useAuth owns the safe user-facing error message.
              } finally {
                setSubmitting(false);
              }
            }}
          >
            <input
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="Username or email"
              autoComplete="username"
              className="w-full rounded-xl border border-aura-text/10 px-4 py-3 text-sm outline-none focus:border-aura-primary"
            />
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Password"
              type="password"
              autoComplete="current-password"
              className="w-full rounded-xl border border-aura-text/10 px-4 py-3 text-sm outline-none focus:border-aura-primary"
            />
            {authError && (
              <div className="rounded-xl bg-error/10 px-4 py-3 text-sm font-semibold text-error">
                {authError}
              </div>
            )}
          <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={submitting}
              data-primary-action="true"
              className="shadow-[0_14px_32px_rgba(0,104,85,0.28)] ring-2 ring-aura-primary/20 hover:ring-aura-primary/35 focus-visible:ring-4 focus-visible:ring-aura-primary/35"
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <button
            type="button"
            className="w-full text-center text-xs font-bold text-aura-primary underline-offset-4 hover:underline"
            onClick={() => {
              setResetOpen((open) => !open);
              setResetMessage(null);
              setResetError(null);
            }}
          >
            Forgot password?
          </button>

          {resetOpen && (
            <form
              className="space-y-3 rounded-xl border border-aura-text/10 bg-surface p-3"
              onSubmit={async (event) => {
                event.preventDefault();
                setResetMessage(null);
                setResetError(null);
                if (!resetIdentifier.trim()) {
                  setResetError('Enter your username or email.');
                  return;
                }
                setResetSubmitting(true);
                try {
                  await sendPasswordResetForIdentifier(resetIdentifier.trim());
                  setResetMessage('If that account exists, a reset link has been sent.');
                } catch {
                  setResetError('Password reset is unavailable. Try again shortly.');
                } finally {
                  setResetSubmitting(false);
                }
              }}
            >
              <label htmlFor="password-reset-identifier" className="block text-xs font-semibold text-text-muted">
                Reset username or email
              </label>
              <input
                id="password-reset-identifier"
                value={resetIdentifier}
                onChange={(event) => setResetIdentifier(event.target.value)}
                autoComplete="username"
                className="w-full rounded-xl border border-aura-text/10 px-4 py-3 text-sm outline-none focus:border-aura-primary"
              />
              {resetMessage && (
                <div className="rounded-xl bg-aura-accent/20 px-4 py-3 text-sm font-semibold text-aura-primary">
                  {resetMessage}
                </div>
              )}
              {resetError && (
                <div className="rounded-xl bg-error/10 px-4 py-3 text-sm font-semibold text-error">
                  {resetError}
                </div>
              )}
              <Button type="submit" variant="secondary" fullWidth disabled={resetSubmitting}>
                {resetSubmitting ? 'Sending...' : 'Send reset link'}
              </Button>
            </form>
          )}

          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={signIn}
            className="min-w-[280px]"
            fullWidth
          >
            Sign in with Google
          </Button>
        </div>
      )}

      <div className="mt-12 text-text-muted text-[11px] text-center">
        By signing in, you agree to the group rules and privacy policy.
      </div>

      {runtimeEnv.allowManualTestTools && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <button
            type="button"
            disabled={seeding}
            className="text-[10px] font-medium text-text-muted/40 hover:text-aura-primary transition-colors underline-offset-2 hover:underline"
            onClick={async () => {
              setSeeding(true);
              setSeedError(null);
              try {
                const response = await fetch('/api/dev/seed', { method: 'POST' });
                const data = await response.json() as { success: boolean; error?: string };
                if (data.success) {
                  setSeedError(null);
                } else {
                  setSeedError(data.error || 'Seed failed');
                }
              } catch {
                setSeedError('Seed request failed. Is the dev server running?');
              } finally {
                setSeeding(false);
              }
            }}
          >
            {seeding ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-full border border-current border-t-transparent animate-spin" />
                Seeding emulator data...
              </span>
            ) : (
              '\u26A0\uFE0F Seed Emulator Data'
            )}
          </button>
          {seedError && (
            <div className="rounded-lg bg-error/10 px-3 py-1.5 text-[10px] font-medium text-error max-w-[280px] text-center">
              {seedError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
